import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera } from "lucide-react";

const API = `${window.location.origin}/api`;

const GOLD = "#C4A671";
const CHUNK = 512 * 1024;
const WINES = ["Blanche", "Scilla", "Terra Arsa"];

const inputCls =
  "w-full bg-[#111] border border-[#2a2a2a] px-4 py-3 text-[14px] text-[#F2EFE8] outline-none focus:border-[#C4A671] transition-colors";

async function compressImage(file) {
  try {
    const bmp = await createImageBitmap(file);
    const maxSide = 2400;
    const scale = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    return blob || file;
  } catch {
    return file;
  }
}

export default function ChefUploadForm({ code, info, onClose }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dishName, setDishName] = useState("");
  const [ingredients, setIngredients] = useState("");
  const [wine, setWine] = useState("Blanche");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(null);
  const fileRef = useRef(null);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!file) return toast.error("Carichi o scatti una fotografia del piatto.");
    if (!dishName.trim() || !ingredients.trim())
      return toast.error("Nome del piatto e ingredienti sono necessari.");
    setBusy(true);
    setProgress(3);
    try {
      const blob = await compressImage(file);
      const total = Math.max(1, Math.ceil(blob.size / CHUNK));
      const { data: init } = await axios.post(`${API}/registry/upload/init`, {
        code, filename: file.name || "photo.jpg", total_chunks: total,
      });
      for (let i = 0; i < total; i++) {
        const part = blob.slice(i * CHUNK, (i + 1) * CHUNK);
        await axios.put(`${API}/registry/upload/${init.upload_id}/chunk/${i}`, part, {
          headers: { "Content-Type": "application/octet-stream" },
        });
        setProgress(5 + Math.round(((i + 1) / total) * 80));
      }
      const { data } = await axios.post(`${API}/registry/upload/complete`, {
        upload_id: init.upload_id, code,
        dish_name: dishName.trim(), ingredients: ingredients.trim(), wine,
      });
      setProgress(100);
      setDone(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Caricamento non riuscito. Riprovi.");
      setProgress(0);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="text-center" data-testid="chef-upload-done">
        <h3 className="font-serif-display text-[26px] leading-tight text-[#F2EFE8]">
          Creazione <span className="italic font-light" style={{ color: GOLD }}>trasmessa.</span>
        </h3>
        <p className="mt-4 text-[14px] leading-[1.85] font-light" style={{ color: "#C9C3B5" }}>
          La perizia sensoriale della Maison è in corso. La Sua creazione sarà
          iscritta al Registro dopo l&apos;approvazione, con la propria Matrice
          delle Affinità Sensoriali.
        </p>
        <p className="mt-4 overline text-[9px]" style={{ color: "#8B7A55" }}>
          Registrazioni rimanenti sul Suo codice: {done.remaining}
        </p>
        <button
          data-testid="chef-upload-close"
          onClick={onClose}
          className="mt-8 px-8 py-3 text-[10px] tracking-[0.24em] uppercase"
          style={{ background: GOLD, color: "#0A0A0A" }}
        >
          Chiudi
        </button>
      </div>
    );
  }

  return (
    <div data-testid="chef-upload-form">
      <span className="block text-center overline text-[9px]" style={{ color: "#8B7A55" }}>
        Allocazione verificata · {info?.business_name || "Maison partner"}
      </span>
      <h3 className="mt-2 font-serif-display text-[24px] leading-tight text-[#F2EFE8] text-center">
        Iscriva la Sua <span className="italic font-light" style={{ color: GOLD }}>creazione.</span>
      </h3>
      <p className="mt-3 mb-6 text-[12.5px] leading-[1.8] font-light text-center" style={{ color: "#C9C3B5" }}>
        Una fotografia, il nome del piatto e gli ingredienti principali.
        Al resto pensa la Maison.
      </p>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={pick}
        className="hidden"
        data-testid="chef-photo-input"
      />
      <button
        data-testid="chef-photo-pick"
        onClick={() => fileRef.current?.click()}
        className="w-full border transition-colors duration-300 overflow-hidden"
        style={{ borderColor: preview ? "rgba(196,166,113,0.5)" : "#2a2a2a" }}
      >
        {preview ? (
          <img src={preview} alt="Anteprima piatto" className="w-full aspect-[4/3] object-cover" />
        ) : (
          <span className="flex flex-col items-center justify-center gap-2 py-10 text-[#7A7568]">
            <Camera size={26} strokeWidth={1.2} style={{ color: GOLD }} />
            <span className="overline text-[9px]">Scatti o carichi la fotografia del piatto</span>
          </span>
        )}
      </button>

      <div className="flex flex-col gap-3 mt-4">
        <input
          data-testid="chef-dish-name"
          className={inputCls}
          placeholder="Nome della creazione *"
          maxLength={120}
          value={dishName}
          onChange={(e) => setDishName(e.target.value)}
        />
        <textarea
          data-testid="chef-ingredients"
          className={`${inputCls} resize-none`}
          rows={2}
          placeholder="Ingredienti principali (separati da virgola) *"
          maxLength={500}
          value={ingredients}
          onChange={(e) => setIngredients(e.target.value)}
        />
        <div>
          <span className="overline text-[9px] text-[#7A7568] block mb-2">Vino in abbinamento</span>
          <div className="flex gap-2">
            {WINES.map((w) => (
              <button
                key={w}
                data-testid={`chef-wine-${w.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setWine(w)}
                className="flex-1 px-2 py-2.5 text-[9px] uppercase tracking-[0.18em] border transition-colors duration-300"
                style={{
                  borderColor: wine === w ? GOLD : "#2a2a2a",
                  color: wine === w ? GOLD : "#7a7a7a",
                  background: wine === w ? "rgba(196,166,113,0.07)" : "transparent",
                }}
              >
                {w}
              </button>
            ))}
          </div>
        </div>
      </div>

      {busy && (
        <div className="mt-5" data-testid="chef-upload-progress">
          <div className="h-[3px] w-full bg-[#1c1c1c]">
            <div className="h-full transition-all duration-300" style={{ width: `${progress}%`, background: GOLD }} />
          </div>
          <p className="mt-2 overline text-[8.5px] text-center" style={{ color: "#8B7A55" }}>
            Trasmissione riservata in corso… {progress}%
          </p>
        </div>
      )}

      <button
        data-testid="chef-upload-submit"
        onClick={submit}
        disabled={busy}
        className="mt-6 w-full py-3.5 text-[10px] tracking-[0.24em] uppercase transition disabled:opacity-40"
        style={{ background: GOLD, color: "#0A0A0A" }}
      >
        {busy ? "Trasmissione…" : "Trasmetta alla Maison"}
      </button>
      <p className="mt-4 overline text-[8px] text-center" style={{ color: "#5a5548" }}>
        Registrazioni disponibili: {info?.remaining ?? "—"} · La pubblicazione avviene dopo la perizia della Maison
      </p>
    </div>
  );
}
